import { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createItemTemplate,
  deleteItemTemplate,
  listItemTemplates,
  updateItemTemplate,
} from "@/features/invoices/api";
import type { InvoiceItemTemplate } from "@/features/invoices/types";
import { PageHeader } from "@/components/common/page-header";
import { TabShell } from "@/features/settings/components/tab-shell";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { ApiError } from "@/lib/api";
import { centsToDollars, dollarsToCents, formatCents } from "@/lib/utils";

const schema = Yup.object({
  name: Yup.string().trim().required("Name is required.").max(120, "Too long"),
  body: Yup.string().max(2000, "Too long"),
  amountDollars: Yup.string()
    .required("Amount is required.")
    .matches(/^\d+(\.\d{1,2})?$/, "Enter a valid dollar amount."),
});

interface TemplateFormValues {
  name: string;
  body: string;
  amountDollars: string;
}

function TemplateDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: InvoiceItemTemplate;
  onSaved: (t: InvoiceItemTemplate) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle>
          <DialogDescription>
            A reusable line-item preset — its description and amount prefill a new invoice line.
          </DialogDescription>
        </DialogHeader>
        <Formik<TemplateFormValues>
          initialValues={{
            name: editing?.name ?? "",
            body: editing?.body ?? "",
            amountDollars: editing ? centsToDollars(editing.amountCents) : "",
          }}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values) => {
            const fields = {
              name: values.name.trim(),
              body: values.body,
              amountCents: dollarsToCents(values.amountDollars),
            };
            try {
              const saved = editing
                ? await updateItemTemplate(editing.id, fields)
                : await createItemTemplate(fields);
              toast.success(editing ? "Template updated." : "Template created.");
              onSaved(saved);
              onOpenChange(false);
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : "Couldn't save the template.");
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="tpl-name">Name</Label>
                <Input
                  id="tpl-name"
                  value={values.name}
                  onChange={(e) => setFieldValue("name", e.target.value)}
                />
                {touched.name && errors.name && <p className="text-destructive text-sm">{errors.name}</p>}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="tpl-body">Description</Label>
                <Textarea
                  id="tpl-body"
                  rows={2}
                  value={values.body}
                  onChange={(e) => setFieldValue("body", e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="tpl-amount">Default Amount</Label>
                <Input
                  id="tpl-amount"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={values.amountDollars}
                  onChange={(e) => setFieldValue("amountDollars", e.target.value)}
                />
                {touched.amountDollars && errors.amountDollars && (
                  <p className="text-destructive text-sm">{errors.amountDollars}</p>
                )}
              </div>
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceTemplatesTab() {
  const { isAdmin } = useFirmSettings();
  const [templates, setTemplates] = useState<InvoiceItemTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InvoiceItemTemplate | undefined>();
  const [deleting, setDeleting] = useState<InvoiceItemTemplate | undefined>();

  useEffect(() => {
    listItemTemplates()
      .then(setTemplates)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter(
      (t) => t.name.toLowerCase().includes(term) || t.body.toLowerCase().includes(term),
    );
  }, [templates, search]);

  function handleSaved(saved: InvoiceItemTemplate) {
    setTemplates((prev) => {
      const idx = prev.findIndex((t) => t.id === saved.id);
      if (idx === -1) return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name));
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteItemTemplate(deleting.id);
      setTemplates((prev) => prev.filter((t) => t.id !== deleting.id));
      toast.success("Template deleted.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete the template.");
    } finally {
      setDeleting(undefined);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Invoice Item Templates"
        subtitle="Reusable line-item presets for invoices."
      />
      <TabShell loading={loading} error={error}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to="/settings/invoices" className="text-muted-foreground hover:text-primary text-sm">
          ← Back to Invoices
        </Link>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search templates"
            className="max-w-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isAdmin && (
            <Button
              size="sm"
              onClick={() => {
                setEditing(undefined);
                setDialogOpen(true);
              }}
            >
              <Plus /> New Template
            </Button>
          )}
        </div>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              {isAdmin && <TableHead className="w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell className="text-muted-foreground">{t.body}</TableCell>
                <TableCell className="text-right">{formatCents(t.amountCents)}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Edit template"
                      onClick={() => {
                        setEditing(t);
                        setDialogOpen(true);
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Delete template"
                      onClick={() => setDeleting(t)}
                    >
                      <Trash2 className="text-destructive size-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 4 : 3} className="text-muted-foreground text-center">
                  No templates yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <TemplateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={handleSaved}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This can&apos;t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </TabShell>
    </div>
  );
}
